import moment, { Moment } from 'moment'

import { emptyCharacter } from '../components/EmptyChar'
import { config } from '../config'
import { DateHandler } from './date-handler'
import { holidays } from './date-properties'

const sunday = 0
const saturday = 6
const weekendDays = [saturday, sunday]

type MomentDateHandler = DateHandler<Moment>

const addDays: MomentDateHandler['addDays'] = ({ days, date }) => newDate(date?.toDate()).add(days, 'days')

const addMonths: MomentDateHandler['addMonths'] = ({ months, date }) => newDate(date?.toDate()).add(months, 'months')

const endOfDay: MomentDateHandler['endOfDay'] = (date) => date.endOf('day')

const formatDate: MomentDateHandler['formatDate'] = ({ date, format = config.defaultDateFormat }) =>
  date.isValid() ? date.format(format) : emptyCharacter

const formatIso: MomentDateHandler['formatIso'] = ({ date, format = config.isoDateFormat }) => date.format(format)

const formatTime: MomentDateHandler['formatTime'] = ({ date, format = config.defaultTimeFormat }) =>
  date.isValid() ? date.utc().format(format) : emptyCharacter

const formatDateTime: MomentDateHandler['formatDateTime'] = ({ date }) =>
  date.isValid() ? `${formatDate({ date })} - ${formatTime({ date })}` : emptyCharacter

const isAfter: MomentDateHandler['isAfter'] = ({ firstDate, secondDate }) => firstDate.isAfter(secondDate)

const isBefore: MomentDateHandler['isBefore'] = ({ firstDate, secondDate }) => firstDate.isBefore(secondDate)

const isSame: MomentDateHandler['isSame'] = ({ firstDate, sameDate }) => firstDate.isSame(sameDate, 'day')

const isSameOrAfter: MomentDateHandler['isSameOrAfter'] = ({ firstDate, secondDate }) =>
  firstDate.isSameOrAfter(secondDate)

const isSameOrBefore: MomentDateHandler['isSameOrBefore'] = ({ firstDate, beforeDate }) =>
  firstDate.isSameOrBefore(beforeDate)

const isWeekend: MomentDateHandler['isWeekend'] = (date) => weekendDays.includes(date.day())

const isWeekendOrHoliday: MomentDateHandler['isWeekendOrHoliday'] = (date) =>
  isWeekend(date) || !!holidays.isHoliday(newDate(date.toDate()).toDate())

const newDate: MomentDateHandler['newDate'] = (date?) => moment(date)

const now: MomentDateHandler['now'] = () => moment(new Date())

const sortByDate: MomentDateHandler['sortByDate'] = ({ firstDate, secondDate }) => firstDate.diff(secondDate)

const startOfDay: MomentDateHandler['startOfDay'] = (date) => date.startOf('day')

const toDate: MomentDateHandler['toDate'] = (date) => date.toDate()

const transformDateToIsoFormat: MomentDateHandler['transformDateToIsoFormat'] = (date) => {
  const inputDateFormat = {
    ddMmYy: config.americanDateFormat,
    ddMmYyyy: config.defaultDateFormat,
    yyyyMmDd: config.isoDateFormat,
  }

  const hasSlash = String(date).indexOf('/') > -1
  const lastSlashSplitValueLength = hasSlash && String(date).split('/')[2].length

  if (lastSlashSplitValueLength === 2) {
    return moment(date, inputDateFormat.ddMmYy).format(config.isoDateFormat)
  }

  if (lastSlashSplitValueLength === 4) {
    return moment(date, inputDateFormat.ddMmYyyy).format(config.isoDateFormat)
  }

  return moment(date, inputDateFormat.yyyyMmDd).format(config.isoDateFormat)
}

export const momentDateHandler: MomentDateHandler = {
  addDays,
  addMonths,
  endOfDay,
  formatDate,
  formatIso,
  formatTime,
  formatDateTime,
  isAfter,
  isBefore,
  isSame,
  isSameOrAfter,
  isSameOrBefore,
  isWeekend,
  isWeekendOrHoliday,
  newDate,
  now,
  sortByDate,
  startOfDay,
  toDate,
  transformDateToIsoFormat,
}
